import Box from '@totallymoney/ui/components/Box'
import Hero from '@totallymoney/ui/components/Hero'
import LinkButton from '@totallymoney/ui/components/LinkButton'
// @ts-ignore
import TrustBox from '@totallymoney/ui/components/TrustBox'
import React from 'react'

import { getAppConfig } from '@utils/getAppConfig'
import { getRedirectAccountUrl } from '@utils/getRedirectAccountUrl'

import { LoanProductCategory } from '@appTypes/LoansApi.interface'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'

import carFinanceHeroImage from '../../../../public/images/car-finance-hero.png'
import loansHeroImage from '../../../../public/images/loans-hero.png'

interface UnscoredPageHeaderProps {
    activeCategoryId: LoanProductCategory | null
}

function getCategoryTitle(categoryId: LoanProductCategory | null) {
    switch (categoryId) {
        case LoanProductCategory.PoorCredit:
            return 'Compare loans for bad credit'
        case LoanProductCategory.CarFinance:
            return 'Compare car finance loans'
        case LoanProductCategory.Secured:
            return 'Compare homeowner loans'
        case LoanProductCategory.Guarantor:
            return 'Compare guarantor loans'
        case LoanProductCategory.AlternativeProducts:
            return 'Compare alternative products'
        case LoanProductCategory.Personal:
        default:
            return 'Find your perfect loan'
    }
}

function getCategoryIllustration(categoryKey: string) {
    if (categoryKey === 'CarFinance') {
        return carFinanceHeroImage?.src
    }
    return loansHeroImage?.src
}

const UnscoredPageHeader = ({ activeCategoryId }: UnscoredPageHeaderProps) => {
    const { appQueryState } = useAppQueryState()
    const illustrationSrc = getCategoryIllustration(activeCategoryId!)
    const logEvents = useLogEvents()
    const appConfig = getAppConfig()

    const formUrl = React.useMemo(() => {
        const data = {
            queryObject: {
                category: appQueryState.category,
                loanAmount: Number(appQueryState.loanAmount),
                loanTerm: Number(appQueryState.loanTerm),
                client: appConfig.NAME_WHITELABEL,
            },
        }
        return getRedirectAccountUrl(data)
    }, [activeCategoryId])

    const onCheckEligibilityClick = (
        event: React.SyntheticEvent<Element, Event>
    ) => {
        event.preventDefault()

        logEvents.logCheckEligibilityEvent({
            category: appQueryState.categoryId!,
            loanAmount: Number(appQueryState.loanAmount!),
            loanTerm: Number(appQueryState.loanTerm!),
            origin: 'header',
        })

        window.location.href = formUrl
    }

    return (
        <>
            <Hero
                headline={getCategoryTitle(activeCategoryId)}
                subtitle="Compare loans and see the lowest rate available to you"
                checkList={[
                    'Boost your chances of approval',
                    'No impact to your credit rating',
                    'Get an instant response',
                ]}
                illustration={illustrationSrc}
                illustrationSrcset={`${illustrationSrc} 600w, ${illustrationSrc} 1024w`}
                illustrationSizes="(min-width: 960px) 720px, 100vw"
            >
                <Box mt={3}>
                    <LinkButton
                        variant="primaryDark"
                        data-gtm-event="loans-formOpen-header"
                        href={formUrl}
                        text="Check my eligibility"
                        onClick={onCheckEligibilityClick}
                    />
                </Box>
            </Hero>

            <Box pt={['space20', 0]} pb="space30" bg="white">
                <TrustBox widgetId="trustpilot-widget" />
            </Box>
        </>
    )
}

export default UnscoredPageHeader
